// A paper row as the editor holds it, and the two crossings: from the server
// (or the bank) into the editor, and from the editor back to the API.
import { isEnabledQuestionType } from '@cwy/config/contracts';
import type { BankQuestionDTO, CustomPaperItem, PaperQuestionType } from '@/types';

export type { PaperQuestionType };

export const QUESTION_TYPE_LABEL: Record<PaperQuestionType, string> = {
  MCQ: 'Multiple choice',
  SHORT_DESC: 'Short answer',
  MEDIUM_DESC: 'Medium answer',
  LONG_DESC: 'Long answer',
};

/** The types a teacher can set — `ENABLED_QUESTION_TYPES`. */
export const QUESTION_TYPES: Array<{ value: PaperQuestionType; label: string }> = (
  Object.keys(QUESTION_TYPE_LABEL) as PaperQuestionType[]
)
  .filter((value) => isEnabledQuestionType(value))
  .map((value) => ({ value, label: QUESTION_TYPE_LABEL[value] }));

/** An old extraction may carry no type; read it off the options and marks. */
function inferredType(item: CustomPaperItem): PaperQuestionType {
  if (item.type) return item.type;
  const options = Array.isArray(item.options)
    ? item.options.filter((option) => option.trim().length > 0)
    : [];
  if (
    options.length >= 2 &&
    item.correctOptionIdx !== null &&
    item.correctOptionIdx !== undefined &&
    item.correctOptionIdx >= 0 &&
    item.correctOptionIdx < options.length
  ) {
    return 'MCQ';
  }
  const marks = item.marks ?? 1;
  if (marks <= 2) return 'SHORT_DESC';
  if (marks <= 4) return 'MEDIUM_DESC';
  return 'LONG_DESC';
}

/** Every optional field filled in, so the editor never branches on `undefined`. */
export function normalizeItem(item: CustomPaperItem, index: number): CustomPaperItem {
  return {
    ...item,
    index,
    type: inferredType(item),
    question: item.question ?? '',
    answer: item.answer ?? '',
    marks: item.marks ?? 1,
    // One hint per question. A row saved with two keeps its first, or its
    // second when that is all it has.
    lightHint: item.lightHint || item.strongHint || '',
    strongHint: '',
    concept: item.concept ?? '',
    options: item.options ?? null,
    correctOptionIdx: item.correctOptionIdx ?? null,
    explanation: item.explanation ?? '',
    markingScheme: item.markingScheme ?? [],
    mistakes: item.mistakes ?? [],
    allocatedSeconds: item.allocatedSeconds ?? null,
    wordLimit: item.wordLimit ?? null,
    videoRef: item.videoRef ?? null,
    subtopicId: item.subtopicId ?? null,
    bankId: item.bankId ?? null,
    aiDrafted: item.aiDrafted ?? [],
  };
}

/**
 * Rows as they arrive, numbered from 1 like everything the server stores.
 *
 * Not `items.map(normalizeItem)`: `map` hands over the array POSITION, which is
 * 0-based, while a row's `index` is what every per-row call sends and the API
 * matches on. Passing the position numbered the cards Q0, Q1… and made
 * "Generate" on a card fill the question above it.
 */
export const fromServer = (items: CustomPaperItem[]): CustomPaperItem[] =>
  items.map((item, position) => normalizeItem(item, position + 1));

/** A new row starts as the first type a teacher can set — multiple choice, for now. */
export function defaultItem(index: number): CustomPaperItem {
  const type = QUESTION_TYPES[0]!.value;
  return normalizeItem(
    {
      index,
      type,
      question: '',
      answer: '',
      marks: 1,
      options: type === 'MCQ' ? ['', '', '', ''] : null,
    },
    index,
  );
}

/** Trim and re-index before a draft crosses the API boundary. */
export function serializeItems(items: CustomPaperItem[]): CustomPaperItem[] {
  return items.map((item, position) => {
    const type = item.type ?? 'SHORT_DESC';
    const originalOptions = Array.isArray(item.options) ? item.options : [];
    const selectedOption =
      type === 'MCQ' && item.correctOptionIdx !== null && item.correctOptionIdx !== undefined
        ? originalOptions[item.correctOptionIdx]?.trim()
        : undefined;
    const options =
      type === 'MCQ' ? originalOptions.map((option) => option.trim()).filter(Boolean) : [];
    const correctOptionIdx = selectedOption ? options.indexOf(selectedOption) : null;

    return {
      ...item,
      index: position + 1,
      type,
      question: item.question.trim(),
      answer: item.answer.trim(),
      marks: item.marks ?? null,
      lightHint: item.lightHint?.trim() ?? '',
      strongHint: '',
      concept: item.concept?.trim() ?? '',
      options: type === 'MCQ' && options.length > 0 ? options : null,
      correctOptionIdx: correctOptionIdx === -1 ? null : correctOptionIdx,
      explanation: item.explanation?.trim() ?? '',
      markingScheme: (item.markingScheme ?? [])
        .map((s) => ({ step: s.step.trim(), marks: Number(s.marks) || 0 }))
        .filter((s) => s.step),
      mistakes: (item.mistakes ?? []).map((m) => m.trim()).filter(Boolean),
      videoRef: item.videoRef?.trim() || null,
    };
  });
}

/** A bank question as a new paper row, TEAM and all. */
export function bankToItem(b: BankQuestionDTO, index: number): CustomPaperItem {
  return normalizeItem(
    {
      index,
      type: b.type,
      question: b.text,
      answer:
        b.type === 'MCQ' && b.options && b.correctOptionIdx !== null
          ? (b.options[b.correctOptionIdx] ?? b.correctAnswer)
          : b.correctAnswer,
      marks: b.maxMarks,
      lightHint: b.tipsText,
      strongHint: b.strongHintText,
      concept: b.subtopicName,
      subtopicId: b.subtopicId,
      options: b.options,
      correctOptionIdx: b.correctOptionIdx,
      explanation: b.explanation,
      markingScheme: b.markingScheme,
      mistakes: b.mistakes,
      // The bank's time was set for the bank; the paper's default rule applies
      // until this teacher sets her own.
      allocatedSeconds: null,
      wordLimit: b.wordLimit,
      videoRef: b.videoRef,
      bankId: b.id,
      aiDrafted: [],
    },
    index,
  );
}

/** What to tell the teacher — the server's own sentence, read off the envelope. */
export { apiErrorMessage as messageFor } from '@/lib/api';
